import React from 'react';
import { ArrowRight, CheckCircle2 } from 'lucide-react';
import SEO from '../components/SEO';
import RelatedPages from '../components/RelatedPages';
import { relatedPagesMap } from '../data/relatedPages';

const ONEDOC_URL = "https://www.onedoc.ch/fr/therapeute-en-nutrition-mco/vevey/pcyv4/zara-nawid";

const RetentionEau = () => {
  return (
    <div style={{ marginTop: '100px' }}>
      <SEO
        title="Rétention d'eau & jambes lourdes — Nutritionniste à Vevey, Clarens & Montreux"
        description="Rétention d'eau, jambes lourdes, gonflements ? Accompagnement nutritionnel personnalisé à Vevey, Clarens et Montreux pour comprendre et apaiser ces symptômes."
        keywords="rétention d'eau femme, jambes lourdes Vevey, gonflement ventre nutritionniste, drainage Clarens Montreux, circulation lymphatique Riviera"
        url="https://nutricentreriviera.ch/retention-eau-jambes-lourdes"
      />

      {/* Hero */}
      <section style={{
        background: 'var(--gradient-hero)',
        padding: '5rem 0 4rem',
        textAlign: 'center'
      }}>
        <div className="container" style={{ maxWidth: '900px' }}>
          <span className="caption" style={{
            color: 'var(--accent-primary)',
            letterSpacing: '0.15em',
            textTransform: 'uppercase',
            fontWeight: 600,
            fontSize: '0.85rem',
            display: 'inline-block',
            marginBottom: '1rem'
          }}>
            Circulation & rétention d'eau
          </span>
          <h1 className="heading-hero" style={{ marginBottom: '1.5rem' }}>
            Rétention d'eau & jambes lourdes : retrouver de la légèreté
          </h1>
          <p className="heading-subtitle" style={{ maxWidth: '780px', margin: '0 auto 2rem', lineHeight: 1.6 }}>
            Vous vous sentez gonflée, vos jambes pèsent, vos chevilles marquent en fin de journée ? Ces signaux ne sont pas anodins — ils reflètent un déséquilibre que l'on peut vraiment travailler. À Vevey, Clarens et Montreux, nous vous accompagnons en profondeur.
          </p>
          <a href={ONEDOC_URL} target="_blank" rel="noopener noreferrer" className="btn-primary" style={{ textDecoration: 'none' }} data-testid="retention-hero-cta">
            Prendre rendez-vous
            <ArrowRight size={20} />
          </a>
        </div>
      </section>

      {/* Vous vous reconnaissez */}
      <section className="section" style={{ background: 'white' }}>
        <div className="container" style={{ maxWidth: '900px' }}>
          <h2 className="heading-2" style={{ marginBottom: '1.5rem', textAlign: 'center' }}>
            Vous vous reconnaissez si…
          </h2>
          <p className="body-large" style={{ textAlign: 'center', color: 'var(--text-muted)', marginBottom: '2.5rem', lineHeight: 1.7 }}>
            Vous n'êtes pas seule. Ces sensations sont parmi les plus fréquentes en consultation — et elles ont presque toujours des causes identifiables.
          </p>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '1rem'
          }}>
            {[
              "Vos jambes deviennent lourdes en fin de journée, surtout l'été ou après une journée debout.",
              "Vos chevilles, vos doigts ou votre visage gonflent — la bague qui ne passe plus, les chaussettes qui marquent.",
              "Votre ventre est régulièrement gonflé, indépendamment de ce que vous mangez.",
              "Vous prenez du poids juste avant les règles, parfois 2 à 3 kg en quelques jours.",
              "Vous avez la sensation que votre corps « retient » — sans comprendre pourquoi.",
              "La circulation au niveau des jambes vous gêne (picotements, froideur, varicosités).",
              "Vous buvez peu… ou au contraire beaucoup, sans amélioration.",
              "Les méthodes habituelles (boire plus d'eau, marcher) ne suffisent plus."
            ].map((s, i) => (
              <div key={i} style={{
                display: 'flex',
                gap: '0.75rem',
                padding: '1.1rem 1.25rem',
                background: 'var(--bg-page)',
                borderRadius: '0.85rem',
                border: '1px solid var(--border-light)',
                alignItems: 'flex-start'
              }}>
                <div style={{
                  width: '8px', height: '8px', borderRadius: '50%',
                  background: 'var(--accent-primary)', flexShrink: 0, marginTop: '0.5rem'
                }} />
                <p className="body-medium" style={{ margin: 0, lineHeight: 1.55 }}>{s}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pourquoi c'est plus complexe */}
      <section className="section" style={{ background: 'var(--bg-section)' }}>
        <div className="container" style={{ maxWidth: '900px' }}>
          <h2 className="heading-2" style={{ marginBottom: '1.5rem' }}>
            Pourquoi la rétention d'eau est plus complexe qu'il n'y paraît
          </h2>
          <p className="body-large" style={{ marginBottom: '1.5rem', lineHeight: 1.7 }}>
            « Boire plus d'eau » ne suffit que rarement. La rétention d'eau et les jambes lourdes sont des symptômes — pas des causes. Derrière, plusieurs mécanismes peuvent être en jeu, parfois combinés :
          </p>
          <ul style={{ listStyle: 'none', padding: 0, display: 'flex', flexDirection: 'column', gap: '0.85rem' }}>
            {[
              ["Un déséquilibre hormonal (œstrogènes/progestérone, cortisol).", "Très fréquent en préménopause, post-partum, ou en fin de cycle."],
              ["Un excès de sodium et de sucres cachés.", "L'alimentation moderne en contient bien plus qu'on ne l'imagine."],
              ["Un déficit en certains micronutriments.", "Magnésium, potassium, vitamines B influencent directement la rétention."],
              ["Une circulation veineuse ou lymphatique fragile.", "Parfois aggravée par la sédentarité, la chaleur, ou la station debout."],
              ["Une inflammation chronique de bas grade.", "Souvent liée à la digestion, au microbiote ou au stress."],
              ["Un foie surchargé.", "Il joue un rôle clé dans l'élimination des fluides et des hormones."]
            ].map(([t, d], i) => (
              <li key={i} style={{
                background: 'white',
                border: '1px solid var(--border-light)',
                borderLeft: '4px solid var(--accent-primary)',
                borderRadius: '0.75rem',
                padding: '1.1rem 1.4rem'
              }}>
                <p className="body-medium" style={{ marginBottom: '0.35rem', fontWeight: 600, color: 'var(--text-primary)' }}>{t}</p>
                <p className="body-medium" style={{ margin: 0, color: 'var(--text-muted)', lineHeight: 1.55 }}>{d}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Notre approche */}
      <section className="section" style={{ background: 'white' }}>
        <div className="container" style={{ maxWidth: '900px' }}>
          <h2 className="heading-2" style={{ marginBottom: '1rem' }}>
            Notre approche à Vevey, Clarens et Montreux
          </h2>
          <p className="body-large" style={{ marginBottom: '2rem', color: 'var(--text-muted)', lineHeight: 1.7 }}>
            Nous ne proposons jamais de « régime drainant » standardisé. Notre travail consiste à <strong>comprendre la cause</strong> de votre rétention pour agir précisément.
          </p>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1.5rem' }}>
            {[
              ["Un bilan complet", "Anamnèse, bilan sanguin si nécessaire, analyse corporelle (composition, eau intra- et extracellulaire via bio-impédancemétrie)."],
              ["Une stratégie nutritionnelle ciblée", "Adaptée à votre profil hormonal, votre digestion, vos habitudes — pas un protocole générique."],
              ["Un suivi dans la durée", "Pour ajuster, mesurer les progrès objectivement et installer un équilibre durable."]
            ].map(([t, d], i) => (
              <div key={i} style={{
                background: 'var(--bg-page)',
                borderRadius: '1rem',
                padding: '1.75rem',
                border: '1px solid var(--border-light)'
              }}>
                <h3 className="heading-3" style={{ marginBottom: '0.75rem', color: 'var(--accent-primary)' }}>{t}</h3>
                <p className="body-medium" style={{ color: 'var(--text-muted)', lineHeight: 1.6, margin: 0 }}>{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section style={{ background: 'var(--accent-primary)', color: 'white', padding: '5rem 0', textAlign: 'center' }}>
        <div className="container" style={{ maxWidth: '720px' }}>
          <h2 className="heading-1" style={{ color: 'white', marginBottom: '1rem' }}>
            Prête à retrouver de la légèreté ?
          </h2>
          <p className="body-large" style={{ color: 'white', opacity: 0.95, marginBottom: '2rem', lineHeight: 1.7 }}>
            Un premier rendez-vous permet d'identifier la vraie cause et de poser les bases d'un accompagnement adapté. Consultations remboursées par certaines assurances complémentaires (ASCA &amp; RME).
          </p>
          <a href={ONEDOC_URL} target="_blank" rel="noopener noreferrer" className="btn-primary"
             style={{ background: 'white', color: 'var(--accent-primary)', textDecoration: 'none' }}
             data-testid="retention-final-cta">
            Réserver ma consultation
            <ArrowRight size={20} />
          </a>
          <p className="caption" style={{
            marginTop: '1rem', color: 'white', opacity: 0.9,
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem'
          }}>
            <CheckCircle2 size={16} />
            Cabinets à Vevey & Clarens (Montreux)
          </p>
        </div>
      </section>

      <RelatedPages items={relatedPagesMap['retention-eau-jambes-lourdes']} />
    </div>
  );
};

export default RetentionEau;
